import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import AddShoppingCartRoundedIcon from "@mui/icons-material/AddShoppingCartRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import LocalFireDepartmentOutlinedIcon from "@mui/icons-material/LocalFireDepartmentOutlined";
import { useParams } from "react-router-dom";

const API_BASE_URL = "http://3.140.128.111/api/v1";

const DEFAULT_THEME = {
  primaryColor: "#b71c1c",
  secondaryColor: "#fdecec",
  buttonColor: "#b71c1c",
  bgColor: "#fff8f8",
  cardColor: "#ffffff",
  inputBg: "#ffffff",
  currencySymbol: "$",
};

const T1MenuDetailsPage = () => {
  const { id } = useParams();
  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);

  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [clientId, setClientId] = useState("");
  const [locationId, setLocationId] = useState("");

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [quantity, setQuantity] = useState(0);

  const sanitizeColor = (value, fallback) => {
    if (!value) return fallback;
    const trimmed = String(value).trim();
    const isHex = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(trimmed);
    const isRgb = /^rgb(a?)\((.*?)\)$/.test(trimmed);
    return isHex || isRgb ? trimmed : fallback;
  };

  useEffect(() => {
    setTheme({
      primaryColor: sanitizeColor(searchParams.get("primaryColor"), DEFAULT_THEME.primaryColor),
      secondaryColor: sanitizeColor(searchParams.get("secondaryColor"), DEFAULT_THEME.secondaryColor),
      buttonColor: sanitizeColor(searchParams.get("buttonColor"), DEFAULT_THEME.buttonColor),
      bgColor: sanitizeColor(searchParams.get("bgColor"), DEFAULT_THEME.bgColor),
      cardColor: sanitizeColor(searchParams.get("cardColor"), DEFAULT_THEME.cardColor),
      inputBg: sanitizeColor(searchParams.get("inputBg"), DEFAULT_THEME.inputBg),
      currencySymbol: searchParams.get("currencySymbol") || DEFAULT_THEME.currencySymbol,
    });

    setClientId(searchParams.get("client_id") || "");
    setLocationId(searchParams.get("location_id") || "");
  }, [searchParams]);

  const getDisplayPrice = (menuItem) => {
    if (menuItem?.pricing) {
      return menuItem.pricing.delivery ?? menuItem.pricing.pickup ?? menuItem.pricing.dine_in ?? null;
    }

    if (Array.isArray(menuItem?.variant_groups) && menuItem.variant_groups.length > 0) {
      const allVariants = menuItem.variant_groups.flatMap((group) => group.variants || []);
      const numericPrices = allVariants
        .map((variant) => variant?.pricing?.delivery ?? variant?.pricing?.pickup ?? variant?.pricing?.dine_in)
        .filter((price) => typeof price === "number");

      if (numericPrices.length > 0) {
        return Math.min(...numericPrices);
      }
    }

    return null;
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined || price === "") return "Price not available";
    return `${theme.currencySymbol}${Number(price).toFixed(2)}`;
  };

  const findItemFromMenuTree = (menuTree, targetItemId) => {
    for (const section of menuTree || []) {
      for (const category of section.categories || []) {
        for (const menuItem of category.items || []) {
          if (menuItem.id === targetItemId) {
            return {
              ...menuItem,
              section_name: section.name,
              category_name: category.name,
            };
          }
        }
      }
    }
    return null;
  };

  useEffect(() => {
    const fetchDetails = async () => {
      if (!clientId || !locationId || !id) return;

      try {
        setLoading(true);
        setError("");

        const response = await fetch(
          `${API_BASE_URL}/menu/${clientId}/locations/${locationId}/tree/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch menu details. Status ${response.status}`);
        }

        const data = await response.json();
        const foundItem = findItemFromMenuTree(data?.menu || [], id);

        if (!foundItem) {
          throw new Error("Item not found for selected branch.");
        }

        setItem(foundItem);
      } catch (err) {
        setError(err.message || "Failed to load item details.");
        setItem(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [clientId, locationId, id]);

  const handleBack = () => {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(
        {
          type: "IFRAME_NAVIGATE",
          path: "/menu",
        },
        "*"
      );
    }
  };

  const price = item ? getDisplayPrice(item) : null;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${theme.bgColor} 0%, ${theme.secondaryColor} 100%)`,
        py: 3,
      }}
    >
      <Container maxWidth="lg">
        <Button
          startIcon={<ArrowBackRoundedIcon />}
          onClick={handleBack}
          sx={{
            mb: 2,
            color: theme.primaryColor,
            fontWeight: 700,
            textTransform: "none",
          }}
        >
          Back to Menu
        </Button>

        {loading && (
          <Box sx={{ py: 10, display: "flex", justifyContent: "center" }}>
            <CircularProgress sx={{ color: theme.primaryColor }} />
          </Box>
        )}

        {error ? <Alert severity="error">{error}</Alert> : null}

        {!loading && item ? (
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              backgroundColor: theme.cardColor,
              border: `1px solid ${theme.secondaryColor}`,
              boxShadow: "0 18px 50px rgba(15, 23, 42, 0.10)",
            }}
          >
            <Grid container>
              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    minHeight: { xs: 280, md: "100%" },
                    background: item.image
                      ? `url(${item.image}) center/cover no-repeat`
                      : `linear-gradient(135deg, ${theme.secondaryColor} 0%, ${theme.bgColor} 100%)`,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={7}>
                <CardContent sx={{ p: { xs: 2.2, md: 3.2 } }}>
                  <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: "wrap" }}>
                    <Chip
                      label={item.is_veg ? "Veg" : "Non-Veg"}
                      sx={{
                        backgroundColor: item.is_veg ? "#DCFCE7" : "#FEE2E2",
                        color: item.is_veg ? "#166534" : "#991B1B",
                        fontWeight: 700,
                      }}
                    />
                    {item.badge ? (
                      <Chip
                        label={item.badge}
                        sx={{
                          backgroundColor: "#FEF3C7",
                          color: "#92400E",
                          fontWeight: 700,
                        }}
                      />
                    ) : null}
                    <Chip
                      label={item.category_name}
                      sx={{
                        backgroundColor: theme.secondaryColor,
                        color: theme.primaryColor,
                        fontWeight: 700,
                      }}
                    />
                  </Stack>

                  <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                    {item.name}
                  </Typography>

                  <Typography variant="body1" sx={{ color: "#64748B", lineHeight: 1.7, mb: 2 }}>
                    {item.description || "Freshly prepared with quality ingredients."}
                  </Typography>

                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: "wrap" }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: theme.primaryColor }}>
                      {formatPrice(price)}
                    </Typography>

                    {item.rating ? (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <StarRoundedIcon sx={{ color: "#F59E0B", fontSize: 18 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.rating} ({item.rating_count || 0})
                        </Typography>
                      </Stack>
                    ) : null}

                    {item.prep_time_minutes ? (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <AccessTimeOutlinedIcon sx={{ color: "#64748B", fontSize: 18 }} />
                        <Typography variant="body2" sx={{ color: "#64748B" }}>
                          {item.prep_time_minutes} min
                        </Typography>
                      </Stack>
                    ) : null}

                    {item.calories ? (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <LocalFireDepartmentOutlinedIcon sx={{ color: "#EF4444", fontSize: 18 }} />
                        <Typography variant="body2" sx={{ color: "#64748B" }}>
                          {item.calories} cal
                        </Typography>
                      </Stack>
                    ) : null}
                  </Stack>

                  {Array.isArray(item.ingredients) && item.ingredients.length > 0 ? (
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                        Ingredients
                      </Typography>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {item.ingredients.map((ingredient) => (
                          <Chip
                            key={ingredient}
                            label={ingredient}
                            sx={{
                              backgroundColor: "#F8FAFC",
                              color: "#334155",
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  ) : null}

                  {Array.isArray(item.variant_groups) && item.variant_groups.length > 0 ? (
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                        Available Variants
                      </Typography>

                      <Stack spacing={1.2}>
                        {item.variant_groups.map((group) => (
                          <Card
                            key={group.id}
                            elevation={0}
                            sx={{
                              p: 1.5,
                              borderRadius: 3,
                              border: `1px solid ${theme.primaryColor}18`,
                              backgroundColor: "#fff",
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                              {group.name}
                            </Typography>

                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                              {(group.variants || []).map((variant) => {
                                const variantPrice =
                                  variant?.pricing?.delivery ??
                                  variant?.pricing?.pickup ??
                                  variant?.pricing?.dine_in;

                                return (
                                  <Chip
                                    key={variant.id}
                                    label={`${variant.name} - ${formatPrice(variantPrice)}`}
                                    sx={{
                                      backgroundColor: theme.secondaryColor,
                                      color: theme.primaryColor,
                                      fontWeight: 700,
                                    }}
                                  />
                                );
                              })}
                            </Stack>
                          </Card>
                        ))}
                      </Stack>
                    </Box>
                  ) : null}

                  <Divider sx={{ my: 2 }} />

                  {quantity > 0 ? (
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <Button
                        variant="outlined"
                        onClick={() => setQuantity((prev) => Math.max(prev - 1, 0))}
                        sx={{
                          minWidth: 48,
                          borderRadius: "12px",
                          borderColor: `${theme.primaryColor}33`,
                          color: theme.primaryColor,
                        }}
                      >
                        <RemoveRoundedIcon />
                      </Button>

                      <Box
                        sx={{
                          minWidth: 70,
                          minHeight: 48,
                          borderRadius: "12px",
                          backgroundColor: theme.secondaryColor,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 800,
                          color: theme.primaryColor,
                        }}
                      >
                        {quantity}
                      </Box>

                      <Button
                        variant="contained"
                        onClick={() => setQuantity((prev) => prev + 1)}
                        sx={{
                          minWidth: 48,
                          borderRadius: "12px",
                          backgroundColor: theme.buttonColor,
                          "&:hover": {
                            backgroundColor: theme.primaryColor,
                          },
                        }}
                      >
                        <AddRoundedIcon />
                      </Button>

                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setQuantity(0)}
                        sx={{
                          minWidth: 48,
                          borderRadius: "12px",
                        }}
                      >
                        <DeleteOutlineRoundedIcon />
                      </Button>
                    </Stack>
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={<AddShoppingCartRoundedIcon />}
                      onClick={() => setQuantity(1)}
                      sx={{
                        minHeight: 48,
                        px: 3,
                        borderRadius: "14px",
                        fontWeight: 700,
                        textTransform: "none",
                        backgroundColor: theme.buttonColor,
                        "&:hover": {
                          backgroundColor: theme.primaryColor,
                        },
                      }}
                    >
                      Add to Cart
                    </Button>
                  )}
                </CardContent>
              </Grid>
            </Grid>
          </Card>
        ) : null}
      </Container>
    </Box>
  );
};

export default T1MenuDetailsPage;